import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React, { useEffect } from "react";

import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { invalidateResourcesRelatedToDeployment } from "@v2/features/page-renderer/components/navbar/invalidateResourcesRelatedToDeployment";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { v2ShopDeploymentRouter } from "@v2/trpc/routes/v2ShopDeployment";

interface DeploymentStatusTagProps {
  file: FileWithLatestFileVersion;
  size?: "sm" | "base";
}

const DEPLOYMENT_STATUSES = [
  "draft",
  "publishing",
  "edited",
  "published",
] as const;
type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number];

const useDeploymentStatus = (
  file: FileWithLatestFileVersion,
): DeploymentStatus | null => {
  const projectId = useCurrentProjectId();
  const { data: allDeployments } =
    v2ShopDeploymentRouter.findDeployments.useQuery(
      projectId ? { projectId } : skipToken,
    );

  if (!projectId || !allDeployments) {
    return null;
  }

  const deploymentsForFile = allDeployments.filter((deployment) => {
    return deployment.filesPublished.some((pf) => pf.id === file.id);
  });

  if (deploymentsForFile.length === 0) {
    if (file.lastPublishedFileVersionId === null) {
      return "draft";
    }
    return file.lastPublishedFileVersionId !== file.latestFileVersion.id
      ? "edited"
      : "published";
  }

  const mostRecentDeployment =
    deploymentsForFile.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })[0] ?? null;

  if (mostRecentDeployment?.deployStatus === "PENDING") {
    return "publishing";
  }

  if (file.lastPublishedFileVersionId === null) {
    return "draft";
  }

  if (file.lastPublishedFileVersionId !== file.latestFileVersion.id) {
    return "edited";
  }

  return "published";
};

export const DeploymentStatusTag = ({
  file,
  size = "base",
}: DeploymentStatusTagProps) => {
  const deploymentStatus = useDeploymentStatus(file);

  // NOTE (Cole, 2025-08-18): This ensures that even if the user hard refreshes the page, this status will still update when publishing finishes.
  useEffect(() => {
    if (deploymentStatus !== "publishing") {
      return;
    }

    const interval = setInterval(() => {
      invalidateResourcesRelatedToDeployment();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [deploymentStatus]);

  switch (deploymentStatus) {
    case "draft":
      return (
        <Tag size={size} variant="neutral">
          Draft
        </Tag>
      );
    case "publishing":
      return (
        <Tag size={size} variant="info">
          Publishing...
        </Tag>
      );
    case "edited":
      return (
        <Tag size={size} variant="info">
          Edited
        </Tag>
      );
    case "published":
      return (
        <Tag size={size} variant="success">
          Published
        </Tag>
      );
    default:
      return null;
  }
};
